// Which slot-calendar panel (if any) is open over the product page. The
// state lives in the URL (?panel=availability | availability-setup) on the
// SAME route, so:
//  - opening pushes one history entry -> the phone/browser back button closes
//    the panel instead of leaving the listing;
//  - closing goes BACK when we opened it (never pushes the product page again,
//    which is what caused the old manage <-> listing back-button loop), or
//    just drops the param when the panel was deep-linked;
//  - switching Setup <-> Manage replaces the entry (one back = close).
import { useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export type SlotCalendarPanel = "manage" | "setup";

const PARAM = "panel";
const VALUE: Record<SlotCalendarPanel, string> = { manage: "availability", setup: "availability-setup" };

/** history.state marker: this panel entry was pushed by the app. */
interface PanelHistoryState {
  slotPanelPushed?: boolean;
}

function parsePanel(value: string | null): SlotCalendarPanel | null {
  if (value === VALUE.manage) return "manage";
  if (value === VALUE.setup) return "setup";
  return null;
}

/** Search string for opening a panel from an arbitrary URL (legacy redirects). */
export function slotCalendarPanelSearch(panel: SlotCalendarPanel): string {
  return `?${PARAM}=${VALUE[panel]}`;
}

export function useSlotCalendarPanel() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const panel = parsePanel(params.get(PARAM));
  const pushedByApp = !!(location.state as PanelHistoryState | null)?.slotPanelPushed;

  const open = useCallback(
    (next: SlotCalendarPanel) => {
      const search = new URLSearchParams(params);
      search.set(PARAM, VALUE[next]);
      const alreadyOpen = panel !== null;
      navigate(
        { pathname: location.pathname, search: `?${search.toString()}` },
        {
          // Switching panels keeps one entry; the first open pushes one.
          replace: alreadyOpen,
          state: { slotPanelPushed: alreadyOpen ? pushedByApp : true } satisfies PanelHistoryState,
        }
      );
    },
    [navigate, location.pathname, params, panel, pushedByApp]
  );

  const close = useCallback(() => {
    if (pushedByApp) {
      navigate(-1);
      return;
    }
    // Deep-linked straight into the panel: nothing of ours to go back to.
    const search = new URLSearchParams(params);
    search.delete(PARAM);
    const qs = search.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" }, { replace: true });
  }, [navigate, location.pathname, params, pushedByApp]);

  return { panel, open, close };
}
